// ---------------------------------------------------------------------------
// DataSetFileFormatField — auto-configures the file selector the backend
// requires for a Data Lake source. It samples the dataset's data items and, in
// the supported shape (one file per item, a single file format across the
// sample), emits one selector named after DataLake's `fileType` with a
// `*.<ext>` glob; the decode mode (`as`) is left unset so the backend infers
// it from the extension. No user choice is needed. Datasets that don't fit
// this shape (multiple files per item, or mixed formats) surface a warning and
// aren't auto-configured, since a single selector would drop data or fail the
// run. A saved selector authored elsewhere (API/CLI) is shown read-only and
// never overwritten.
// ---------------------------------------------------------------------------

import { Body1, Field } from '@fluentui/react-components';
import { useEffect, useState } from 'react';

import type { DataHubService } from '../../../../../services/dataHubService.js';
import type { DataLakeFileSelector } from '../../EvaluationTemplateForm.types.js';
import {
  analyzeDataSetFiles,
  buildDefaultFileSelector,
  extensionFromGlob,
  fileSelectorBindingPath,
  fileSelectorNameForFileType,
} from '../../EvaluationTemplateForm.utils.js';
import type { DataSetFileShape } from '../../EvaluationTemplateForm.utils.js';

export function DataSetFileFormatField({
  dataHubService,
  dataSetId,
  version,
  disabled,
  fileSelector,
  onFileSelectorChange,
}: {
  dataHubService: DataHubService;
  dataSetId: string;
  version: number | null;
  disabled: boolean;
  fileSelector: DataLakeFileSelector[];
  onFileSelectorChange: (selectors: DataLakeFileSelector[]) => void;
}) {
  // null = not yet detected (loading or no version).
  const [shape, setShape] = useState<DataSetFileShape | null>(null);
  const [detectError, setDetectError] = useState<string | null>(null);

  useEffect(() => {
    if (version === null) {
      setShape(null);
      return;
    }
    let cancelled = false;
    setShape(null);
    setDetectError(null);
    dataHubService
      .listDataEntries({
        dataSetId,
        dataSetVersion: version,
        // Sample a page (not just the first item) so cross-item format mixing
        // is caught rather than silently mis-detected from a single row.
        pageSize: 10,
        includeCount: false,
      })
      .then((res) => {
        if (cancelled) return;
        setShape(analyzeDataSetFiles(res.items));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to detect file format.';
        console.warn(
          '[DataSetFileFormatField] failed to detect file format',
          dataSetId,
          version,
          err,
        );
        setDetectError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [dataHubService, dataSetId, version]);

  // A single selector with a `*.ext` glob is the shape we auto-generate. Any
  // other saved selector (multiple entries, or a non-`*.ext` glob) was authored
  // elsewhere and must be preserved verbatim, not clobbered.
  const current = fileSelector.length === 1 ? fileSelector[0] : undefined;
  const recognizedExt =
    current !== undefined ? extensionFromGlob(current.firstMatching) : null;
  const isCustom = fileSelector.length > 0 && recognizedExt === null;

  // The supported shape: exactly one file per item (min === max === 1) and a
  // single format across the sample. Requiring the min guards against a sample
  // where some item has no file — a `*.ext` selector would match nothing for
  // that item and the backend would hard-fail the run.
  const isSupported =
    shape !== null &&
    shape.minFilesPerItem === 1 &&
    shape.maxFilesPerItem === 1 &&
    shape.extensions.length === 1;

  // Auto-generate the single selector once, when nothing is chosen yet.
  useEffect(() => {
    if (disabled || isCustom) return;
    if (fileSelector.length > 0) return;
    if (shape === null || !isSupported) return;
    const ext = shape.extensions[0];
    if (ext === undefined) return;
    onFileSelectorChange([
      buildDefaultFileSelector(
        ext,
        fileSelectorNameForFileType(shape.fileType),
      ),
    ]);
  }, [
    shape,
    isSupported,
    fileSelector.length,
    isCustom,
    disabled,
    onFileSelectorChange,
  ]);

  const firstSelector = fileSelector[0];
  const bindingHint =
    firstSelector !== undefined ? (
      <Body1
        style={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          opacity: 0.75,
        }}
      >
        Reference in prompts as: {fileSelectorBindingPath(firstSelector)}
      </Body1>
    ) : null;

  if (isCustom) {
    return (
      <Field label="File selector">
        <Body1 style={{ fontSize: '0.85rem' }}>
          Using saved file selector
          {fileSelector.map((s) => (
            <span
              key={s.name}
              style={{ fontFamily: 'monospace', display: 'block' }}
            >
              {s.name} → {s.firstMatching}
              {s.as !== undefined ? ` (as ${s.as})` : ''}
            </span>
          ))}
        </Body1>
        {bindingHint}
      </Field>
    );
  }

  // Reasons a dataset can't be auto-configured, in priority order. Each blocks
  // save (no selector is emitted) and explains why.
  const unsupportedMessage =
    detectError !== null
      ? `Couldn't detect the dataset's file format: ${detectError}`
      : shape === null
        ? undefined
        : shape.maxFilesPerItem > 1
          ? 'Each data item has multiple files. Auto-configuration supports one file per item; use a custom fileSelector (API/CLI) to bind several files.'
          : shape.extensions.length === 0
            ? "No files found in this dataset's items — can't auto-configure a selector."
            : shape.extensions.length > 1
              ? `This dataset mixes file formats (${shape.extensions
                  .map((e) => `.${e}`)
                  .join(
                    ', ',
                  )}). A single selector would drop data or fail the run; mixed-format datasets aren't supported yet.`
              : shape.minFilesPerItem < 1
                ? 'Some data items have no file. A single selector would fail the run on those items; every item must have exactly one file to auto-configure.'
                : undefined;

  const detecting = shape === null && detectError === null;

  // Supported shape auto-configures silently, and while detecting there's
  // nothing to show — render nothing so the form stays clean.
  if (detecting || isSupported) {
    return null;
  }

  // Only unsupported shapes (multi-file per item, mixed formats, no files, or
  // a detection error) surface UI: a warning that blocks save and explains why.
  return (
    <Field
      label="File format"
      validationState={detectError !== null ? 'error' : 'warning'}
      validationMessage={unsupportedMessage}
    >
      <Body1 style={{ fontSize: '0.85rem' }}>Not auto-configured</Body1>
    </Field>
  );
}
